/**
 * Ecran de preparation d'une enveloppe : le document en pages continues a
 * gauche, toutes les actions dans un panneau a droite - comme le parcours
 * du signataire (porteur, 06/10/2026 : « rajouter a droite du document
 * toutes les actions comme dans la signature », « arreter les ascenseurs et
 * lister les pages »).
 *
 * Placement (B-08 lot 3.2, decisions du 18/09/2026) : les coordonnees sont
 * des FRACTIONS de page (0 a 1), origine en haut a gauche - le seul repere
 * commun a l'ecran, au modele et a la page PDF reelle. Les cadres sont
 * FACULTATIFS : sans aucun cadre, le document est signe pareil, les
 * signatures figurant sur la page de synthese.
 */
import { useEffect, useRef, useState } from "react";
import type { ReactNode } from "react";

import { api, type Modele, type ReglePages } from "../api";
import { Erreur, Icone, TRACES } from "./ui";
import { DocumentEnFlux } from "./DocumentEnFlux";
import type { TaillePage } from "./PdfViewer";

export type KindChamp = "signature" | "date" | "nom" | "email" | "texte" | "mention" | "case"
  | "paraphe" | "radio" | "texte_fixe" | "heure" | "date_heure" | "liste" | "piece_jointe" | "date_saisie";

/** Lot 3bis : champs remplis par le SIGNATAIRE (les autres le sont par nous). */
const A_SAISIR: readonly KindChamp[] = ["texte", "mention", "case", "radio", "liste", "piece_jointe"];

export interface Cadre {
  /** Identifiant local, le temps de la saisie : la base en donnera un vrai. */
  cle: string;
  signataire: number;
  kind: KindChamp;
  page_no: number;
  x: number;
  y: number;
  w: number;
  h: number;
  libelle?: string;
  obligatoire?: boolean;
  groupe?: string;
  /** Liste deroulante : les choix proposes (deux au moins). */
  options?: string[];
}

interface Props {
  data: ArrayBuffer;
  /** Noms affiches, dans l'ordre de la liste des signataires. */
  signataires: string[];
  /** Le signataire a qui vont les champs poses. */
  actif: number;
  onActif: (i: number) => void;
  cadres: Cadre[];
  onChange: (cadres: Cadre[]) => void;
  onTelecharger: () => void;
  /** Sections du panneau avant les champs (document, signataires). */
  avant: ReactNode;
  /** Sections du panneau apres les champs (options, envoi). */
  apres: ReactNode;
  /** Ligne d'information dans la barre du document (fichier, signataires). */
  entete?: ReactNode;
  /** « Choisir un modele » depuis l'etape precedente : section ouverte. */
  modelesOuverts?: boolean;
}

// Palette (porteur, 11/10/2026, d'apres DocuSign et Universign) : des
// boutons a icone ranges par groupe, sans phrase d'aide. Heure et « date et
// heure » ne sont plus des boutons : ce sont des formats du bouton Date,
// choisis dans la fenetre de reglage.
type Groupe = "Signature" | "Coordonnées" | "Saisies" | "Autre";
const GROUPES: Groupe[] = ["Signature", "Coordonnées", "Saisies", "Autre"];
const TYPES: { kind: KindChamp; libelle: string; groupe: Groupe; icone: string; w: number; h: number; cache?: boolean }[] = [
  { kind: "signature", libelle: "Signature", groupe: "Signature", icone: "M2 12c2-4 3-6 4-6s0 5 1 5 2-3 3-3 1 2 2 2 1-1 2-1M2 14h12", w: 0.26, h: 0.05 },
  { kind: "paraphe", libelle: "Paraphe", groupe: "Signature", icone: "M2 12 4.5 4 7 12M3 9.5h3M9 4v8h2.5a2 2 0 0 0 0-4H9h2a2 2 0 0 0 0-4H9", w: 0.07, h: 0.035 },
  { kind: "nom", libelle: "Nom", groupe: "Coordonnées", icone: "M8 7a2.5 2.5 0 1 0 0-5 2.5 2.5 0 0 0 0 5ZM3 14c0-3 2.2-5 5-5s5 2 5 5", w: 0.26, h: 0.025 },
  { kind: "email", libelle: "E-mail", groupe: "Coordonnées", icone: "M2 4h12v8H2zM2 4l6 5 6-5", w: 0.3, h: 0.025 },
  { kind: "date", libelle: "Date", groupe: "Coordonnées", icone: "M2.5 4h11v9.5h-11zM2.5 7h11M5 2.5v3M11 2.5v3", w: 0.18, h: 0.025 },
  { kind: "heure", libelle: "Heure", groupe: "Coordonnées", icone: "", w: 0.12, h: 0.025, cache: true },
  { kind: "date_heure", libelle: "Date et heure", groupe: "Coordonnées", icone: "", w: 0.26, h: 0.025, cache: true },
  { kind: "date_saisie", libelle: "Date à saisir", groupe: "Coordonnées", icone: "", w: 0.18, h: 0.025, cache: true },
  { kind: "texte", libelle: "Texte", groupe: "Saisies", icone: "M3 3.5h10M8 3.5V13", w: 0.3, h: 0.025 },
  { kind: "mention", libelle: "Mention", groupe: "Saisies", icone: "M3 6h3v3c0 1.5-1 2.5-2 3M9 6h3v3c0 1.5-1 2.5-2 3", w: 0.3, h: 0.025 },
  { kind: "case", libelle: "Case à cocher", groupe: "Saisies", icone: "M2.5 2.5h11v11h-11zM5 8l2 2 4-4", w: 0.025, h: 0.018 },
  { kind: "liste", libelle: "Liste de choix", groupe: "Saisies", icone: "M2.5 4h11v8h-11zM9.5 7l1.5 1.5L12.5 7", w: 0.26, h: 0.025 },
  { kind: "radio", libelle: "Bouton radio", groupe: "Saisies", icone: "M8 13.5a5.5 5.5 0 1 0 0-11 5.5 5.5 0 0 0 0 11ZM8 10a2 2 0 1 0 0-4 2 2 0 0 0 0 4Z", w: 0.025, h: 0.018 },
  { kind: "piece_jointe", libelle: "Pièce jointe", groupe: "Autre", icone: "M10.5 4.5 5 10a1.8 1.8 0 0 0 2.5 2.5L13 7a3 3 0 0 0-4.2-4.2L3.3 8.3", w: 0.22, h: 0.03 },
  { kind: "texte_fixe", libelle: "Texte fixe", groupe: "Autre", icone: "M2 3.5h7M5.5 3.5V12M10 7.5h4M12 7.5V12", w: 0.3, h: 0.025 },
];

/** Paraphe : chaque paraphe se valide d'un clic a lui (sinon un clic les valide tous). */
export const PARAPHE_CHAQUE_PAGE = "chaque_page";

/** Les formats du bouton Date (fenetre de reglage). */
const FORMATS_DATE: { kind: KindChamp; libelle: string }[] = [
  { kind: "date", libelle: "Date" }, { kind: "heure", libelle: "Heure" }, { kind: "date_heure", libelle: "Date et heure" },
];


// Une couleur par signataire : le seul moyen de voir d'un coup d'oeil a
// qui appartient quel cadre. Le rouge est reserve au destructif.
const COULEURS = ["#16324f", "#0f7b6c", "#8a5a00", "#5b3a8e", "#1f6f9e", "#7a5c00"];

export const couleurSignataire = (i: number) => COULEURS[i % COULEURS.length] as string;

const borne = (v: number, min: number, max: number) => Math.min(max, Math.max(min, v));

export function EditeurCadres({ data, signataires, actif, onActif, cadres, onChange, onTelecharger, avant, apres,
                               entete, modelesOuverts }: Props) {
  const [outil, setOutil] = useState<KindChamp>("signature");
  const [zoom, setZoom] = useState(1);
  const [echec, setEchec] = useState(false);
  const glisse = useRef<{ cle: string; mode: "deplace" | "taille"; dx: number; dy: number } | null>(null);
  const [selection, setSelection] = useState<string | null>(null);
  const choisi = cadres.find((c) => c.cle === selection);
  // Porteur, 11/10/2026 : « ne pas accepter la création d'un champ
  // incomplet ». Tant que le champ choisi l'est, on ne pose ni ne choisit
  // rien d'autre, et OK reste grisé.
  const incomplet = (c: Cadre) => (["mention", "texte_fixe", "radio", "piece_jointe"].includes(c.kind) && !c.libelle?.trim())
    || (c.kind === "radio" && !c.groupe?.trim())
    || (c.kind === "liste" && new Set((c.options ?? []).map((o) => o.trim()).filter(Boolean)).size < 2);
  const bloque = choisi !== undefined && incomplet(choisi);
  const regler = (modif: Partial<Cadre>) =>
    onChange(cadres.map((c) => (c.cle === selection ? { ...c, ...modif } : c)));

  // Modeles (B-08 lot 3.3) : la regle de pages est demandee a l'enregistrement.
  const [nbPages, setNbPages] = useState(0);
  const [modeles, setModeles] = useState<Modele[]>([]);
  const [modeleChoisi, setModeleChoisi] = useState("");
  const [enregistrement, setEnregistrement] = useState(false);
  const [nomModele, setNomModele] = useState("");
  const [regle, setRegle] = useState<ReglePages>("absolue");
  const [erreurModele, setErreurModele] = useState<unknown>(null);

  useEffect(() => {
    void (async () => {
      try {
        setModeles((await api.modeles()).modeles);
      } catch {
        // Un confort : leur absence n'empeche pas de poser des cadres.
      }
    })();
  }, []);

  const appliquerModele = async () => {
    const modele = modeles.find((m) => m.id === modeleChoisi);
    if (!modele || !nbPages) return;
    if (cadres.length > 0 &&
        !window.confirm("Appliquer ce modèle remplacera les champs déjà posés. Continuer ?")) return;
    setErreurModele(null);
    try {
      const { champs } = await api.champsDeModele(modele.id, nbPages, signataires.length);
      onChange(champs.map((c, i) => ({
        cle: `modele-${Date.now()}-${i}`,
        signataire: c.signer_index,
        kind: c.kind, page_no: c.page_no, x: c.x, y: c.y, w: c.w, h: c.h,
        libelle: c.libelle ?? undefined, obligatoire: c.obligatoire ?? true, groupe: c.groupe ?? undefined,
        options: c.options ?? undefined,
      })));
    } catch (e) {
      setErreurModele(e);
    }
  };

  const enregistrerModele = async () => {
    setErreurModele(null);
    try {
      await api.creerModele({
        name: nomModele.trim(),
        page_rule: regle,
        source_page_count: nbPages,
        signer_count: signataires.length,
        champs: cadres.map((c) => ({
          signer_index: c.signataire, kind: c.kind, page_no: c.page_no,
          x: c.x, y: c.y, w: c.w, h: c.h, libelle: c.libelle || null, obligatoire: c.obligatoire ?? true,
          groupe: c.groupe || null,
          options: c.kind === "liste" ? (c.options ?? []).map((o) => o.trim()).filter(Boolean) : null,
        })),
      });
      setModeles((await api.modeles()).modeles);
      setEnregistrement(false);
      setNomModele("");
    } catch (e) {
      setErreurModele(e);
    }
  };

  const poser = (taille: TaillePage, e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target !== e.currentTarget) return; // clic sur un cadre existant
    poserA(taille, e.currentTarget.getBoundingClientRect(), e.clientX, e.clientY, outil);
  };

  /** Porteur, 12/10/2026 : « faire glisser les champs sur le document ».
   *  Le bouton deposé sur la page pose son champ a cet endroit ; le clic
   *  (bouton choisi, puis clic sur la page) reste possible. */
  const deposer = (taille: TaillePage, e: React.DragEvent<HTMLDivElement>) => {
    const kind = e.dataTransfer.getData("application/x-sign365-champ") as KindChamp;
    if (!kind) return;
    e.preventDefault();
    setOutil(kind);
    poserA(taille, e.currentTarget.getBoundingClientRect(), e.clientX, e.clientY, kind);
  };

  const poserA = (taille: TaillePage, r: DOMRect, clientX: number, clientY: number, outil: KindChamp) => {
    if (bloque) return;
    const modele = TYPES.find((t) => t.kind === outil);
    if (!modele) return;
    // Le clic designe le CENTRE du cadre : c'est la ou l'oeil vise.
    const x = borne((clientX - r.left) / r.width - modele.w / 2, 0, 1 - modele.w);
    const y = borne((clientY - r.top) / r.height - modele.h / 2, 0, 1 - modele.h);
    const cle = `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
    onChange([...cadres, {
      cle, signataire: actif, kind: outil, page_no: taille.page, x, y, w: modele.w, h: modele.h,
      ...(outil === "mention" ? { libelle: "Lu et approuvé" } : {}),
      ...(outil === "texte_fixe" ? { libelle: "Texte" } : {}),
      ...(outil === "piece_jointe" ? { libelle: "RIB" } : {}),
      // Un nouveau bouton radio rejoint le dernier groupe du signataire.
      ...(outil === "radio" ? {
        groupe: [...cadres].reverse().find((c) => c.kind === "radio" && c.signataire === actif)?.groupe ?? "Choix 1",
      } : {}),
      obligatoire: true,
    }]);
    setSelection(cle);
  };

  const deplacer = (e: React.MouseEvent<HTMLDivElement>) => {
    const g = glisse.current;
    if (!g) return;
    const r = e.currentTarget.getBoundingClientRect();
    const fx = (e.clientX - r.left) / r.width;
    const fy = (e.clientY - r.top) / r.height;
    onChange(cadres.map((c) => {
      if (c.cle !== g.cle) return c;
      if (g.mode === "deplace") {
        return { ...c, x: borne(fx - g.dx, 0, 1 - c.w), y: borne(fy - g.dy, 0, 1 - c.h) };
      }
      // Taille minimale : en dessous, le cadre serait impossible a rattraper.
      return { ...c, w: borne(fx - c.x, 0.015, 1 - c.x), h: borne(fy - c.y, 0.012, 1 - c.y) };
    }));
  };

  const nomDe = (i: number) => signataires[i] || `Signataire ${i + 1}`;
  /** « Signataire : Alice », mais « Remplit : Rémi » tel quel (NewEnvelope
   *  prefixe deja les remplisseurs) - plus de « Signataire : Remplit : … ». */
  const etiquette = (i: number) => (/^(Remplit|Approbateur) : /.test(nomDe(i)) ? nomDe(i) : `Signataire : ${nomDe(i)}`);
  const fenetre = useRef<HTMLDivElement>(null);

  // Porteur, 12/10/2026 : « décaler la page pour voir tout le cadre ».
  // A l'ouverture, la fenetre de reglage prend le focus : Echap la ferme et
  // le clavier y travaille directement (critique impeccable du 13/10/2026).
  useEffect(() => {
    if (!selection) return;
    window.requestAnimationFrame(() => {
      fenetre.current?.scrollIntoView({ block: "nearest", behavior: "smooth" });
      fenetre.current?.focus({ preventScroll: true });
    });
  }, [selection]);

  const imprimer = () => {
    const url = URL.createObjectURL(new Blob([data.slice(0)], { type: "application/pdf" }));
    const cadreImpression = document.createElement("iframe");
    cadreImpression.style.display = "none";
    cadreImpression.src = url;
    cadreImpression.onload = () => cadreImpression.contentWindow?.print();
    document.body.appendChild(cadreImpression);
    window.setTimeout(() => URL.revokeObjectURL(url), 60_000);
  };

  // Porteur, 11/10/2026 : « lorsqu'on pose un champ, une fenêtre de réglage
  // s'ouvre avec le choix du signataire et les options du bouton » - sur le
  // document, a cote du champ, et non plus en bas du panneau.
  const fenetreReglage = (choisi: Cadre) => (
    <div
      ref={fenetre}
      className="reglage reglage--flottant"
      role="dialog"
      aria-label={`Réglage du champ ${TYPES.find((t) => t.kind === choisi.kind)?.libelle ?? ""}`}
      tabIndex={-1}
      style={{ left: `${Math.min(choisi.x, 0.62) * 100}%`,
               ...(choisi.y > 0.7 ? { bottom: `${(1 - choisi.y) * 100 + 1}%` } : { top: `${(choisi.y + choisi.h) * 100 + 1}%` }) }}
      onMouseDown={(e) => e.stopPropagation()}
      onKeyDown={(e) => {
        // Echap ferme, comme OK : un champ incomplet reste ouvert (regle du 11/10/2026).
        if (e.key === "Escape" && !bloque) {
          e.stopPropagation();
          setSelection(null);
        }
      }}
    >
      <p className="reglage__titre">
        {TYPES.find((t) => t.kind === choisi.kind)?.libelle} · page {choisi.page_no}
      </p>
      <select className="select select--compact" value={choisi.signataire} aria-label="Signataire"
              onChange={(e) => regler({ signataire: Number(e.target.value) })}>
        {signataires.map((_, i) => <option key={i} value={i}>{etiquette(i)}</option>)}
      </select>
      {(choisi.kind === "date" || choisi.kind === "date_saisie") && (
        // Porteur, 11/10/2026 : « Saisie : Automatique / Manuel ».
        <select className="select select--compact" value={choisi.kind} aria-label="Saisie"
                onChange={(e) => regler({ kind: e.target.value as KindChamp })}>
          <option value="date">Saisie : automatique</option>
          <option value="date_saisie">Saisie : par le signataire</option>
        </select>
      )}
      {FORMATS_DATE.some((f) => f.kind === choisi.kind) && (
        <select className="select select--compact" value={choisi.kind} aria-label="Format"
                onChange={(e) => {
                  const t = TYPES.find((x) => x.kind === e.target.value)!;
                  regler({ kind: t.kind, w: Math.min(t.w, 1 - choisi.x) });
                }}>
          {FORMATS_DATE.map((f) => <option key={f.kind} value={f.kind}>Format : {f.libelle}</option>)}
        </select>
      )}
      {choisi.kind === "radio" && (
        <label className="champ-court">
          <span>Groupe (les options d'un même groupe s'excluent)</span>
          <input className="input" value={choisi.groupe ?? ""} maxLength={80}
                 onChange={(e) => regler({ groupe: e.target.value })}
                 aria-invalid={!choisi.groupe?.trim() ? true : undefined} />
        </label>
      )}
      {choisi.kind === "liste" && (
        <label className="champ-court">
          <span>Choix proposés (un par ligne)</span>
          <textarea className="input" rows={4} value={(choisi.options ?? []).join("\n")}
                    onChange={(e) => regler({ options: e.target.value.split("\n") })}
                    aria-invalid={new Set((choisi.options ?? []).map((o) => o.trim()).filter(Boolean)).size < 2 ? true : undefined} />
        </label>
      )}
      {choisi.kind === "texte_fixe" && (
        <label className="champ-court">
          <span>Texte affiché</span>
          <input className="input" value={choisi.libelle ?? ""} maxLength={200}
                 onChange={(e) => regler({ libelle: e.target.value })}
                 aria-invalid={!choisi.libelle?.trim() ? true : undefined} />
        </label>
      )}
      {choisi.kind === "paraphe" && (
        // Porteur, 12/10/2026 : l'emetteur choisit si un clic valide tous les
        // paraphes du signataire, ou s'il faut cliquer chacun. Porte par
        // `groupe` sur TOUS ses paraphes (« chaque_page ») - ce champ ne sert
        // sinon qu'aux boutons radio.
        <label className="champ-court">
          <span>Validation par le signataire</span>
          <select className="select" value={choisi.groupe === PARAPHE_CHAQUE_PAGE ? PARAPHE_CHAQUE_PAGE : ""}
                  onChange={(e) => onChange(cadres.map((c) => (c.kind === "paraphe" && c.signataire === choisi.signataire
                    ? { ...c, groupe: e.target.value || undefined } : c)))}>
            <option value="">Un clic valide tous ses paraphes</option>
            <option value={PARAPHE_CHAQUE_PAGE}>Un clic sur chaque paraphe</option>
          </select>
        </label>
      )}
      {choisi.kind === "paraphe" && nbPages > 1 && (() => {
        // Porteur, 13/10/2026 : on doit voir si l'option est active. Un
        // interrupteur, avec le nombre de pages paraphees.
        const siens = (c: Cadre) => c.kind === "paraphe" && c.signataire === choisi.signataire;
        const pages = new Set(cadres.filter(siens).map((c) => c.page_no)).size;
        const partout = pages >= nbPages;
        return (
          <label className="case-ligne">
            <input type="checkbox" checked={partout}
                   onChange={() => onChange(partout
                     // Retire les paraphes de ce signataire des AUTRES pages.
                     ? cadres.filter((c) => !siens(c) || c.page_no === choisi.page_no)
                     // Le meme paraphe, au meme endroit, sur chaque page.
                     : [...cadres, ...Array.from({ length: nbPages }, (_, p) => p + 1)
                       .filter((p) => !cadres.some((c) => siens(c) && c.page_no === p))
                       .map((p) => ({ ...choisi, cle: `${choisi.cle}-p${p}`, page_no: p }))])} />
            Sur toutes les pages ({pages}/{nbPages})
          </label>
        );
      })()}
      {(A_SAISIR.includes(choisi.kind) || choisi.kind === "paraphe") && (
        <>
          {choisi.kind !== "paraphe" && (
          <label className="champ-court">
            <span>{choisi.kind === "mention" ? "Phrase à recopier"
              : choisi.kind === "case" || choisi.kind === "radio" ? "Intitulé"
        : choisi.kind === "liste" ? "Question"
          : choisi.kind === "piece_jointe" ? "Document demandé (ex. : RIB)" : "Consigne"}</span>
            <input
              className="input"
              value={choisi.libelle ?? ""}
              maxLength={200}
              onChange={(e) => regler({ libelle: e.target.value })}
              placeholder={choisi.kind === "texte" ? "Ex. : Fonction du signataire" : ""}
              aria-invalid={(choisi.kind === "mention" || choisi.kind === "radio") && !choisi.libelle?.trim() ? true : undefined}
            />
          </label>
          )}
          <label className="case-ligne">
            <input type="checkbox" checked={choisi.obligatoire ?? true}
                   onChange={(e) => regler({ obligatoire: e.target.checked })} />
            Obligatoire
          </label>
        </>
      )}
      {bloque && (
        <p className="muted">
          {choisi.kind === "liste" ? "Proposez au moins deux choix"
            : `Donnez un texte à ce champ${choisi.kind === "radio" ? " (intitulé de l'option et groupe)" : ""}`} avant de continuer.
        </p>
      )}
      <div className="reglage__actions">
        <button type="button" className="reglage__supprimer" aria-label="Supprimer ce champ" title="Supprimer"
                onClick={() => { onChange(cadres.filter((x) => x.cle !== choisi.cle)); setSelection(null); }}>
          <Icone d="M3 4.5h10M6.5 4.5V3h3v1.5M4.5 4.5l.7 8.5h5.6l.7-8.5M7 7v4M9 7v4" />
        </button>
        <button type="button" className="btn btn--secondary" disabled={bloque}
                onClick={() => {
                  // Dupliquer (DocuSign) : la copie juste dessous, choisie aussitot.
                  const cle = `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
                  // Une option radio copiee est une AUTRE option : son intitule
                  // est a saisir (le champ reste bloque jusque-la).
                  onChange([...cadres, { ...choisi, cle, y: borne(choisi.y + choisi.h + 0.01, 0, 1 - choisi.h),
                                         ...(choisi.kind === "radio" ? { libelle: "" } : {}) }]);
                  setSelection(cle);
                }}>
          Dupliquer
        </button>
        <button type="button" className="btn btn--primary" disabled={bloque} onClick={() => setSelection(null)}>OK</button>
      </div>
    </div>
  );

  return (
    <div className="prepa">
      <div className="prepa__document">
        {/* Porteur, 11/10/2026 : barre centree, texte plus gros et plus
            contraste, Telecharger et Imprimer ecrits en toutes lettres. */}
        <div className="prepa__outils">
          {entete && <span className="prepa__entete">{entete}</span>}
          <span className="prepa__pages">{nbPages ? `${nbPages} page${nbPages > 1 ? "s" : ""}` : "…"}</span>
          <span className="barre-outils__groupe">
            <button type="button" aria-label="Réduire" onClick={() => setZoom((z) => Math.max(0.5, +(z - 0.1).toFixed(2)))}>−</button>
            <span className="barre-outils__zoom">{Math.round(zoom * 100)} %</span>
            <button type="button" aria-label="Agrandir" onClick={() => setZoom((z) => Math.min(2, +(z + 0.1).toFixed(2)))}>+</button>
          </span>
          <button type="button" className="btn btn--secondary" onClick={onTelecharger}>Télécharger</button>
          <button type="button" className="btn btn--secondary" onClick={imprimer}>Imprimer</button>
        </div>
        {echec ? (
          <p className="muted">
            Le document n'a pas pu être affiché : vous pouvez l'envoyer, les signatures
            figureront sur la page de synthèse.
          </p>
        ) : (
          <DocumentEnFlux
            data={data}
            zoom={zoom}
            onPages={setNbPages}
            onEchec={() => setEchec(true)}
            calque={(taille) => (
              <div
                className="calque-cadres"
                onMouseDown={(e) => poser(taille, e)}
                onDragOver={(e) => { if (e.dataTransfer.types.includes("application/x-sign365-champ")) e.preventDefault(); }}
                onDrop={(e) => deposer(taille, e)}
                onMouseMove={deplacer}
                onMouseUp={() => { glisse.current = null; }}
                onMouseLeave={() => { glisse.current = null; }}
              >
                {cadres.filter((c) => c.page_no === taille.page).map((c) => {
                  const couleur = couleurSignataire(c.signataire);
                  return (
                    <div
                      key={c.cle}
                      className={c.cle === selection ? "cadre cadre--choisi" : "cadre"}
                      style={{
                        left: `${c.x * 100}%`, top: `${c.y * 100}%`,
                        width: `${c.w * 100}%`, height: `${c.h * 100}%`,
                        borderColor: couleur, color: couleur,
                      }}
                      onMouseDown={(e) => {
                        e.stopPropagation();
                        if (bloque && c.cle !== selection) return;
                        setSelection(c.cle);
                        const r = e.currentTarget.parentElement!.getBoundingClientRect();
                        glisse.current = {
                          cle: c.cle, mode: "deplace",
                          dx: (e.clientX - r.left) / r.width - c.x,
                          dy: (e.clientY - r.top) / r.height - c.y,
                        };
                      }}
                    >
                      {c.kind === "case" || c.kind === "radio" ? (
                        // Porteur, 12/10/2026 (« visuel HS ») : la case ou le rond,
                        // et son intitule ecrit a droite, hors du cadre.
                        <>
                          <span className={c.kind === "radio" ? "cadre__coche cadre__coche--rond" : "cadre__coche"} />
                          {c.libelle && <span className="cadre__intitule" style={{ color: couleur }}>{c.libelle}</span>}
                        </>
                      ) : (
                        <span className="cadre__texte">
                          {c.libelle || TYPES.find((t) => t.kind === c.kind)?.libelle}
                        </span>
                      )}
                      <button
                        type="button"
                        className="cadre__supprimer"
                        aria-label="Supprimer ce champ"
                        onMouseDown={(e) => e.stopPropagation()}
                        onClick={() => onChange(cadres.filter((x) => x.cle !== c.cle))}
                      >
                        <Icone d={TRACES.croix} taille={12} />
                      </button>
                      <span
                        className="cadre__poignee"
                        style={{ background: couleur }}
                        onMouseDown={(e) => {
                          e.stopPropagation();
                          glisse.current = { cle: c.cle, mode: "taille", dx: 0, dy: 0 };
                        }}
                      />
                    </div>
                  );
                })}
                {choisi && choisi.page_no === taille.page && fenetreReglage(choisi)}
              </div>
            )}
          />
        )}
      </div>

      <aside className="prepa__panneau">
        {avant}

        <section className="panneau">
          <h2 className="panneau__titre">Faire glisser les champs sur le document</h2>
          <label className="champ-court">
            <span className="sr-only">Signataire des champs posés</span>
            <select className="select palette__signataire" value={actif}
                    style={{ borderColor: couleurSignataire(actif), color: couleurSignataire(actif) }}
                    onChange={(e) => onActif(Number(e.target.value))}>
              {signataires.map((_, i) => <option key={i} value={i}>{etiquette(i)}</option>)}
            </select>
          </label>
          {GROUPES.map((g) => (
            <div key={g}>
              <p className="palette__groupe">{g}</p>
              <div className="palette">
                {TYPES.filter((t) => t.groupe === g && !t.cache).map((t) => (
                  <button
                    key={t.kind}
                    type="button"
                    className={t.kind === outil ? "palette__champ palette__champ--actif" : "palette__champ"}
                    aria-pressed={t.kind === outil}
                    draggable
                    onDragStart={(e) => {
                      e.dataTransfer.setData("application/x-sign365-champ", t.kind);
                      e.dataTransfer.effectAllowed = "copy";
                    }}
                    onClick={() => setOutil(t.kind)}
                  >
                    <Icone d={t.icone} />
                    <span>{t.libelle}</span>
                  </button>
                ))}
              </div>
            </div>
          ))}

          <p className="muted panneau__aide">
            {cadres.length === 0
              ? "Aucun champ posé."
              : `${cadres.length} champ${cadres.length > 1 ? "s" : ""} posé${cadres.length > 1 ? "s" : ""}.`}
          </p>

          <details className="replie" open={modelesOuverts || undefined}>
            <summary>Modèles</summary>
            <div className="stack-serre">
              <div className="ligne">
                <select className="select" value={modeleChoisi} onChange={(e) => setModeleChoisi(e.target.value)}
                        aria-label="Modèle de placement">
                  <option value="">— aucun —</option>
                  {modeles.map((m) => (
                    <option key={m.id} value={m.id}>
                      {m.name} ({m.field_count} champ{m.field_count > 1 ? "s" : ""})
                    </option>
                  ))}
                </select>
                <button type="button" className="btn btn--secondary" disabled={!modeleChoisi || !nbPages}
                        onClick={() => void appliquerModele()}>
                  Appliquer
                </button>
              </div>
              {!enregistrement ? (
                <button type="button" className="btn btn--link" disabled={cadres.length === 0 || !nbPages}
                        onClick={() => setEnregistrement(true)}>
                  Enregistrer ces champs comme modèle
                </button>
              ) : (
                <>
                  <input className="input" value={nomModele} onChange={(e) => setNomModele(e.target.value)}
                         placeholder="Nom du modèle" autoFocus />
                  {/* Question posee A L'ENREGISTREMENT (decision du 18/09/2026). */}
                  <label className="champ-court">
                    <span>Si le document n'a pas {nbPages} page{nbPages > 1 ? "s" : ""}</span>
                    <select className="select" value={regle} onChange={(e) => setRegle(e.target.value as ReglePages)}>
                      <option value="absolue">garder le numéro de page</option>
                      <option value="premiere">tout sur la 1re page</option>
                      <option value="derniere">tout sur la dernière</option>
                      <option value="refuser">refuser le document</option>
                    </select>
                  </label>
                  <div className="actions">
                    <button type="button" className="btn btn--primary" disabled={!nomModele.trim()}
                            onClick={() => void enregistrerModele()}>
                      Enregistrer
                    </button>
                    <button type="button" className="btn btn--secondary"
                            onClick={() => { setEnregistrement(false); setErreurModele(null); }}>
                      Annuler
                    </button>
                  </div>
                </>
              )}
              {erreurModele !== null && <Erreur erreur={erreurModele} />}
            </div>
          </details>
        </section>

        {apres}
      </aside>
    </div>
  );
}
